import { getAuthContextState } from '@/uptk/core/api/auth_context_store';
import { getPortalTypeFromPath } from '@/utils/route_builder';
import type { ThemeMode } from '@/types/theme_configuration';
import { is_theme_mode } from '@/types/theme_configuration';

export const theme_mode_changed_event_name = 'geode:theme-mode-change';

export const public_theme_routes = [
  '/',
  '/login',
  '/register',
  '/sign-up',
  '/debug',
  '/about',
  '/terms',
  '/privacy',
  '/consent',
  '/help',
  '/noaccess',
  '/documentation',
  '/simulation',
] as const;

export type ThemeModeStorageKey = 'selectedTheme' | 'publicTheme';

const transient_theme_modes = new Map<string, ThemeMode>();

/** La préférence de portail ne traverse jamais une identité non résolue. */
export const portal_theme_mode_key = () => {
  const context = getAuthContextState();
  const tenant = context?.tenantId?.trim(); const user = context?.userId?.trim();
  return tenant && user ? `geode:theme-mode:${tenant}:${user}` : null;
};
export const system_theme_mode = (): ThemeMode => typeof document !== 'undefined' && getComputedStyle(document.documentElement).getPropertyValue('--geode-system-light').trim() === '1' ? 'light' : 'dark';
export const read_portal_theme_mode = (): ThemeMode => {
  try {
    const key = portal_theme_mode_key();
    if (key && transient_theme_modes.has(key)) return transient_theme_modes.get(key)!;
    const stored = key && typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
    return is_theme_mode(stored) ? stored : system_theme_mode();
  } catch { return system_theme_mode(); }
};
const read_public_theme_mode = () => {
  try { return window.localStorage.getItem('publicTheme'); } catch { return null; }
};


export const is_public_theme_route = (pathname: string) =>
  public_theme_routes.some((route) => {
    if (pathname === route || pathname.startsWith(`${route}?`)) {
      return true;
    }
    return route !== '/' && pathname.startsWith(`${route}/`);
  });

export const resolve_theme_mode_for_path = (
  pathname: string,
  selectedTheme?: string | null
): ThemeMode => {
  if (typeof window === 'undefined') {
    return is_public_theme_route(pathname) ? 'light' : 'dark';
  }

  const storedPublicTheme = read_public_theme_mode();
  const portalTheme = is_theme_mode(selectedTheme) ? selectedTheme : read_portal_theme_mode();

  if (is_public_theme_route(pathname)) {
    return is_theme_mode(storedPublicTheme) ? storedPublicTheme : 'light';
  }

  return portalTheme;
};

export const apply_document_theme_classes = (pathname: string, nextTheme?: ThemeMode) => {
  const themeName = nextTheme ?? resolve_theme_mode_for_path(pathname);
  if (typeof document === 'undefined') {
    return themeName;
  }

  const htmlRoot = document.documentElement;
  const publicRoute = is_public_theme_route(pathname);
  const portalTypeClass = `portal-type-${getPortalTypeFromPath(pathname)}`;
  const portalTypeClasses = Array.from(htmlRoot.classList).filter((className) =>
    className.startsWith('portal-type-')
  );
  const domainClasses = Array.from(htmlRoot.classList).filter((className) =>
    className.startsWith('domain-')
  );
  const domainClass = pathname.toLowerCase().includes('/finance/')
    ? 'domain-finance'
    : null;

  htmlRoot.classList.remove(
    'dark',
    'light',
    'portal-theme',
    ...portalTypeClasses,
    ...domainClasses
  );
  htmlRoot.classList.add(themeName);

  if (!publicRoute) {
    htmlRoot.classList.add('portal-theme');
    htmlRoot.classList.add(portalTypeClass);
    if (domainClass) {
      htmlRoot.classList.add(domainClass);
    }
  }

  return themeName;
};

const normalize_tenant_attribute_value = (tenantId?: string | null) => {
  if (typeof tenantId !== 'string') {
    return '';
  }

  const normalizedTenantId = tenantId.trim();
  if (!normalizedTenantId) {
    return '';
  }

  return normalizedTenantId;
};

export const apply_document_theme_state = (
  pathname: string,
  nextTheme?: ThemeMode,
  tenantId?: string | null
) => {
  const themeName = apply_document_theme_classes(pathname, nextTheme);
  if (typeof document === 'undefined') {
    return themeName;
  }

  const htmlRoot = document.documentElement;
  const normalizedTenantId = normalize_tenant_attribute_value(tenantId);
  if (normalizedTenantId) {
    htmlRoot.setAttribute('data-tenant', normalizedTenantId);
  } else {
    htmlRoot.removeAttribute('data-tenant');
  }

  return themeName;
};

export const store_theme_mode = (storageKey: ThemeModeStorageKey, nextTheme: ThemeMode) => {
  if (typeof window === 'undefined') {
    return;
  }

  const resolvedKey = storageKey === 'selectedTheme' ? portal_theme_mode_key() : storageKey;
  try { if (resolvedKey) { window.localStorage.setItem(resolvedKey, nextTheme); transient_theme_modes.delete(resolvedKey); } } catch { if (resolvedKey) transient_theme_modes.set(resolvedKey, nextTheme); }
  window.dispatchEvent(
    new CustomEvent(theme_mode_changed_event_name, {
      detail: {
        storageKey,
        themeMode: nextTheme,
      },
    })
  );
};
